import { Link as RouterLink } from 'react-router-dom';
// @mui
import {
  Box, Tooltip, Link, ListItemText, useTheme,
} from '@mui/material';
// locales
import { forwardRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import {
  StyledItem, StyledIcon, StyledDotIcon, TNavItemProps, StyledItemHorizontal,
} from '@mgm/common/layout/main';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { useFeature } from '../../../hooks/useFeature';
import { TSettingPermissions } from '../../../types/settings.type';
import { useAppSelector } from '../../../hooks/useAppStore';
import { getEmployeePermissions, getIntegrations, getManagerPermissions } from '../../../store/settings/selectors';
import { getAuthenticateUser } from '../../../store/authenticate/selectors';
import { ROUTES } from '../../../data';

const NavItem = forwardRef<HTMLDivElement, TNavItemProps>(
  ({
    item, depth, open, active, isExternalLink, ...other
  }, ref) => {
    const { t } = useTranslation('nav');
    const { allowRouteAccess, allowFeatureAccess } = useFeature();

    const managerSettings = useAppSelector(getManagerPermissions);
    const employeeSettings = useAppSelector(getEmployeePermissions);
    const { personnel } = useAppSelector(getAuthenticateUser);
    const theme = useTheme() as TCustomTheme;
    const { isMobile } = useCustomTheme(theme);
    const integrations = useAppSelector(getIntegrations);

    const {
      title, path, icon, value, info, children, disabled, caption,
    } = item;

    const isAdmin = personnel?.permissionLevel === 'Admin';
    const isManager = personnel?.permissionLevel === 'Manager';
    const isEmployee = personnel?.permissionLevel === 'Employee';

    const checkPermission = (
      perform :string,
      roleBasedSettings: TSettingPermissions,
      permissionKey: string,
      requiredPermission = 'view',
    ) => get(roleBasedSettings, `${perform}.${permissionKey}.${requiredPermission}`);

    const allowedEmailsForInventory = ['paradiso@managemate.org', 'info@shopigold.com', 'gotlib14@gmail.com', 'k.turjeman@gluskainc.com'];

    const isDisplayRoute = useMemo(() => {
      if (path === ROUTES.INVENTORY) {
        return allowedEmailsForInventory.includes(personnel?.email);
      }

      if (path === ROUTES.COMMISSION_TEAMS && allowFeatureAccess('teamCommissions')) {
        return true;
      }

      if ((path === ROUTES.ANALYTICS.COMMISSION_INDIVIDUAL || path === ROUTES.ANALYTICS.COMMISSION_TEAMS || path === ROUTES.ANALYTICS.ROOT) && allowFeatureAccess('analytics')) {
        return true;
      }

      if (path === ROUTES.ANALYTICS.DOR) {
        return !!integrations?.Dor?.integrated;
      }

      if (path === ROUTES.HELP) return true;

      if (isMobile && path === ROUTES.CALENDAR) return false;

      return (
        allowRouteAccess(path)
      && (isAdmin
        || (isManager && checkPermission(
          value === 'timeoff' ? 'actions' : 'visibility',
          managerSettings,
          value || '',
          value === 'timeoff' ? 'manage' : 'view',
        ))
        || (isEmployee && checkPermission(
          value === 'timeoff' ? 'actions' : 'visibility',
          employeeSettings,
          value || '',
          value === 'timeoff' ? 'manage' : 'view',
        )))
      );
    }, [
      allowRouteAccess,
      path,
      isAdmin,
      isManager,
      isEmployee,
      managerSettings,
      employeeSettings,
      value,
      personnel.email,
      allowedEmailsForInventory,
      isMobile,
      integrations,
    ]);

    if (!isDisplayRoute) {
      return null;
    }

    const subItem = depth !== 1;

    const isNavHorizontal = window !== window.parent;

    const DynamicStyledItem = window !== window.parent ? StyledItemHorizontal : StyledItem;

    const renderContent = (
      <DynamicStyledItem ref={ref} depth={depth} active={active} disabled={disabled} caption={!!caption} {...other}>
        {icon && <StyledIcon>{icon}</StyledIcon>}

        {subItem && !isNavHorizontal && (
          <StyledIcon>
            <StyledDotIcon active={active && subItem} />
          </StyledIcon>
        )}

        <ListItemText
          primary={`${t(title)}`}
          secondary={
          caption && (
            <Tooltip title={`${t(caption)}`} placement="top-start">
              <span>{`${t(caption)}`}</span>
            </Tooltip>
          )
        }
          primaryTypographyProps={{
            noWrap: true,
            component: 'span',
            pl: 2,
            variant: active ? 'subtitle2' : 'body2',
          }}
          secondaryTypographyProps={{
            noWrap: true,
            variant: 'caption',
          }}
        />

        {info && (
        <Box component="span" sx={{ lineHeight: 0 }}>
          {info}
        </Box>
        )}

        {!!children && (
          open ? (
            <KeyboardArrowDownIcon sx={{ ml: 1, flexShrink: 0, fontSize: 16 }} />
          ) : (
            <KeyboardArrowRightIcon sx={{ ml: 1, flexShrink: 0, fontSize: 16 }} />
          )
        )}
      </DynamicStyledItem>
    );

    const renderItem = () => {
    // ExternalLink
      if (isExternalLink) {
        return (
          <Link href={path} target="_blank" rel="noopener" underline="none">
            {renderContent}
          </Link>
        );
      }

      if (children) {
        return renderContent;
      }

      return (
        <Link component={RouterLink} to={path} underline="none">
          {renderContent}
        </Link>
      );
    };

    return renderItem();
  },
);

export {
  NavItem,
};
